import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { extractApiErrorMessage } from '../utils/errorHandler';
import {
  signInToFirebase,
  signOutFromFirebase,
  waitForFirebaseAuthReady,
} from '../services/firebaseAuthBridge';
import { normalizeAuthUser } from './authUser';

interface User {
  userId: string;
  userName: string;
  email: string;
  deptCode: string;
  deptName: string;
  positionId: string;
  positionName: string;
  roles: string[];
  permissions?: string[]; // 使用者權限列表
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  permissions: string[]; // 當前使用者的權限列表
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshPermissions: () => Promise<void>; // 重新載入權限
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);

  const getStoredToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  const buildAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for token in localStorage or sessionStorage
      const token = getStoredToken();
      const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');

      if (token && savedUser) {
        // Verify token with backend
        try {
          const response = await axios.get(buildApiUrl('/api/auth/verify'), {
            headers: buildAuthHeaders(),
          });
          if (response.data.status !== 'success') {
            clearAuthData();
            return;
          }

          // Firebase Auth has its own persistent session. On page reload we wait
          // for it to restore; if it didn't (private mode, storage cleared, first
          // visit after this feature shipped), recover by minting a fresh custom
          // token from the backend before exposing the user — otherwise child
          // subscribers would race ahead and hit permission-denied.
          const fbUser = await waitForFirebaseAuthReady();
          if (!fbUser) {
            try {
              const tokenRes = await axios.post(
                buildApiUrl('/api/v1/auth/firebase-token'),
                {},
                { headers: buildAuthHeaders() }
              );
              const fbToken = tokenRes.data?.data?.firebaseCustomToken;
              if (tokenRes.data?.status === 'success' && fbToken) {
                await signInToFirebase(fbToken);
              }
            } catch (err) {
              console.warn('[AuthContext] Firebase Auth recovery failed; Firestore realtime disabled', err);
            }
          }

          const userData = normalizeAuthUser(JSON.parse(savedUser));
          setUser(userData);

          // 載入使用者權限
          await loadUserPermissions(userData);
        } catch (error) {
          // Token verification failed, clear storage
          clearAuthData();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await axios.post(buildApiUrl('/api/auth/login'), {
        username,
        password,
        rememberMe
      });

      if (response.data.status === 'success') {
        const { token, user, firebaseCustomToken, roles } = response.data.data;
        const normalizedUser = normalizeAuthUser(user, roles);

        // Store token and user info
        if (rememberMe) {
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(normalizedUser));
        } else {
          sessionStorage.setItem('token', token);
          sessionStorage.setItem('user', JSON.stringify(normalizedUser));
        }

        // Sign in to Firebase BEFORE setUser so that downstream subscribers
        // (useFirestoreSync, useInbox) never race against an unauthenticated
        // Firestore connection.
        await signInToFirebase(firebaseCustomToken);

        // Update state
        setUser(normalizedUser);

        // 載入使用者權限
        await loadUserPermissions(normalizedUser);
      } else {
        const errorMessage = extractApiErrorMessage(response.data, '登入失敗');
        throw new Error(errorMessage);
      }
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, '登入失敗'));
    }
  };

  const logout = async () => {
    await signOutFromFirebase();
    clearAuthData();
    setUser(null);
    setPermissions([]);
    window.location.href = '/login';
  };

  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  };

  const isAuthenticated = !!user;

  // 載入使用者權限
  const loadUserPermissions = async (targetUser?: User | null) => {
    try {
      const response = await axios.get(buildApiUrl('/api/v2/users/current/permissions'), {
        headers: buildAuthHeaders(),
      });
      if (response.data.success && response.data.data) {
        const effectivePermissions = response.data.data.effectivePermissions || [];
        // 轉換權限格式
        const permissionList = effectivePermissions.flatMap((perm: any) => {
          const permissions = [];
          if (perm.query) permissions.push(`${perm.pgCode}_QUERY`);
          if (perm.addNew) permissions.push(`${perm.pgCode}_ADD`);
          if (perm.edit) permissions.push(`${perm.pgCode}_EDIT`);
          if (perm.delete || perm.del) permissions.push(`${perm.pgCode}_DELETE`);
          if (perm.export) permissions.push(`${perm.pgCode}_EXPORT`);
          if (perm.print || perm.printer) permissions.push(`${perm.pgCode}_PRINT`);
          if (perm.approve) permissions.push(`${perm.pgCode}_APPROVE`);
          return permissions;
        });
        
        setPermissions(permissionList);
        
        // 同時更新 user 物件中的權限
        if (targetUser) {
          setUser({ ...targetUser, permissions: permissionList });
        } else if (user) {
          setUser({ ...user, permissions: permissionList });
        }
      }
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      // 權限載入失敗不影響使用者登入狀態
      setPermissions([]);
    }
  };

  // 重新載入權限
  const refreshPermissions = async () => {
    await loadUserPermissions();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      permissions,
      login,
      logout,
      checkAuth,
      refreshPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
